// Improved version with reduced redundancy

import React, { useState, useCallback, useMemo } from "react";
import { useOrganization, useOrganizationList } from "@clerk/clerk-react";
import { Plus, X, Building2, Mail, Globe } from "lucide-react";
import toast from "react-hot-toast";
import ApiService from "../services/ApiService";
import { useOrganizationCreation } from "../contexts/OrganizationContext";
// Utility functions
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validateDomain = (domain) => {
  const cleanDomain = domain.trim();
  let domainToValidate = cleanDomain;

  if (cleanDomain.startsWith("http://") || cleanDomain.startsWith("https://")) {
    try {
      const url = new URL(cleanDomain);
      domainToValidate = url.hostname;
    } catch {
      return false;
    }
  }

  domainToValidate = domainToValidate.replace(/\/$/, "");
  const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  return domainRegex.test(domainToValidate);
};

// CSS classes extracted for reusability
const INPUT_CLASSES = {
  base: "w-full px-4 py-3 bg-white border-2 rounded-xl font-medium text-slate-900 placeholder-slate-400 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-slate-400",
  error: "border-red-300 focus:ring-red-100 focus:border-red-500 bg-red-50",
  normal: "border-slate-200",
  disabled: "opacity-60 cursor-not-allowed"
};

const retryWithBackoff = async (fn, maxRetries = 3) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await fn();
      if (result?.success || result?.conflict) return result;
      
      if (result?.retry && attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, result.wait_time || 2000));
        continue;
      }
      return result;
    } catch (error) {
      if (attempt === maxRetries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, 1500 * (attempt + 1)));
    }
  }
};

// Generic form input component - moved outside to prevent recreation on each render
const FormInput = React.memo(({ 
  label, 
  icon: Icon, 
  iconColor, 
  value, 
  onChange, 
  error, 
  disabled, 
  ...inputProps 
}) => {
  const inputClassName = `${INPUT_CLASSES.base} ${
    error ? INPUT_CLASSES.error : INPUT_CLASSES.normal
  } ${disabled ? INPUT_CLASSES.disabled : ""}`;

  return (
    <div className="group">
      <label className="block text-sm font-semibold text-slate-700 mb-2 transition-colors group-focus-within:text-blue-600">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 ${iconColor} rounded-md group-focus-within:opacity-80 transition-colors`}>
            <Icon className="h-4 w-4" />
          </div>
          {label}
        </div>
      </label>
      <div className="relative">
        <input
          value={value}
          onChange={onChange}
          className={inputClassName}
          disabled={disabled}
          {...inputProps}
        />
        {error && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse"></div>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
          <span className="block h-1 w-1 bg-red-500 rounded-full"></span>
          {error}
        </p>      )}
    </div>
  );
});

// Set display name for debugging
FormInput.displayName = 'FormInput';

export function CustomCreateOrganization({ onSuccess, children, isModal = true }) {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", domain: "", email: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { createOrganization, setActive } = useOrganizationList();
  const { setIsCreatingOrganization } = useOrganizationCreation();

  // Memoized individual field handlers for maximum stability
  const handleNameChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleDomainChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, domain: e.target.value }));
  }, []);

  const handleEmailChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, email: e.target.value }));
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Organization name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Support email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.domain.trim()) {
      newErrors.domain = "Domain is required";
    } else if (!validateDomain(formData.domain)) {
      newErrors.domain = "Please enter a valid domain";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setIsOpen(false);
    setFormData({ name: "", domain: "", email: "" });
    setErrors({});
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    if (!createOrganization) {
      toast.error("Organization creation unavailable. Please refresh.");
      return;
    }

    setIsLoading(true);
    setIsCreatingOrganization(true); // Mark as creating organization
    const toastId = "org-creation";

    try {
      // Create organization in Clerk
      toast.loading("Creating organization...", { id: toastId });
      const clerkOrg = await createOrganization({ name: formData.name.trim() });

      // Set as active (this will redirect to dashboard)
      toast.loading("Setting up context...", { id: toastId });
      await setActive({ organization: clerkOrg.id });

      // Save to backend (dashboard shows loading state)
      const formDataObj = new FormData();
      Object.entries({
        org_name: formData.name.trim(),
        org_domain: formData.domain.trim(),
        org_support_email: formData.email.trim()
      }).forEach(([key, value]) => formDataObj.append(key, value));

      toast.loading("Saving details...", { id: toastId });
      
      const result = await retryWithBackoff(() => ApiService.createOrganization(formDataObj));

      toast.dismiss(toastId);

      if (result?.conflict) {
        toast.error(`Already associated with "${result.existing_organization?.name}"`, { duration: 6000 });
        return;
      }

      toast.success(result?.success ? "Organization created!" : "Organization created! Syncing...");
      resetForm();
      onSuccess?.(clerkOrg);

    } catch (err) {
      console.error("Organization creation error:", err);
      toast.dismiss(toastId);
      toast.error(err.message || "Failed to create organization");    } finally {
      setIsLoading(false);
      setIsCreatingOrganization(false); // Clear creating state
    }
  };

  const formJSX = useMemo(() => (
    <form key="org-creation-form" onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-1 space-y-6">
        <FormInput
          label="Organization Name"
          icon={Building2}
          iconColor="bg-blue-50 text-blue-600"
          type="text"
          value={formData.name}
          onChange={handleNameChange}
          placeholder="Enter your organization name"
          error={errors.name}
          disabled={isLoading}
        />

        <FormInput
          label="Website Domain"
          icon={Globe}
          iconColor="bg-emerald-50 text-emerald-600"
          type="text"
          value={formData.domain}
          onChange={handleDomainChange}
          placeholder="example.com"
          error={errors.domain}
          disabled={isLoading}
        />

        <FormInput
          label="Support Email"
          icon={Mail}
          iconColor="bg-purple-50 text-purple-600"
          type="email"
          value={formData.email}
          onChange={handleEmailChange}
          placeholder="support@example.com"
          error={errors.email}
          disabled={isLoading}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 mt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={resetForm}
          disabled={isLoading}
          className="flex-1 py-2.5 px-4 bg-white border-2 border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50 transition-all"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 py-2.5 px-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-800 disabled:opacity-50 transition-all shadow-md"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              Creating...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Building2 className="h-4 w-4" />
              Create Organization
            </span>
          )}
        </button>      </div>
    </form>
  ), [formData, errors, isLoading, handleSubmit, handleNameChange, handleDomainChange, handleEmailChange, resetForm]);

  if (!isModal) return formJSX;

  return (
    <>
      <div onClick={() => setIsOpen(true)} className="cursor-pointer">
        {children}
      </div>
      
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6 sm:p-8">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] flex flex-col shadow-2xl ring-1 ring-black/5">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-5 py-4 border-b border-slate-200 rounded-t-2xl">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Create Organization</h2>
                  <p className="text-sm text-slate-600">Set up your workspace</p>
                </div>
                <button
                  onClick={resetForm}
                  disabled={isLoading}
                  className="p-2 hover:bg-white/80 rounded-lg transition-all disabled:opacity-50"
                >
                  <X className="h-4 w-4 text-slate-500" />
                </button>
              </div>
            </div>            
            {/* Form */}
            <div className="px-5 py-4 flex-1 overflow-y-auto">
              {formJSX}
            </div>
          </div>
        </div>
      )}
    </>
  );
}